/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import Button from '@/components/Button';
import Information from '@/components/Information';
import Markdown from '@/components/Markdown';
import ToastContent from '@/components/ToastContent';

import { PassoExecutado } from '@/models/PassoExecutado';
import {
  ProtocoloExecutado,
  RedirecionamentoProtocolo,
} from '@/models/ProtocoloExecutado';

import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import ErrorBox from '@/features/enterprise/components/ErrorBox';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';
import { useIntegrationContext } from '@/features/enterprise/contexts/IntegrationContext';
import {
  CondicaoSolicitacaoType,
  FhirArtefatoType,
} from '@/features/enterprise/models/types';
import IntegracaoService from '@/features/enterprise/services/IntegracaoService';
import {
  getPassoRespostaPayload,
  isValidRespostaPayload,
  listInvalidRespostaPayload,
} from '@/features/execucao/helpers/respostas';
import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';
import Feature from '@/features/features/components/Feature';

import { ModoRedirecionamento } from '@/models/OpcaoRedirecionamento';

import { Solicitacao } from '@/models/Solicitacao';
import { ExecutarPassoPayload, RespostaPayload } from '../../types';

import { usePerguntaContext } from '../../providers/PerguntaProvider';
import ExecucaoAPI from '../../services/ExecucaoAPI';

import ButtonInterromperProtocolo from '../ButtonInterromperProtocolo';
import ButtonOpenFlowchart from '../ButtonOpenFlowchart';
import ButtonSairProtocolo from '../ButtonSairProtocolo';
import ConditionAlert from '../ConditionAlert';
import PassoExecutadoBreakpoint from '../PassoExecutadoBreakpoint';

import ProtocoloExecutadoSumario from '../ProtocoloExecutadoSumario';
import BoxPerguntas, { BoxPerguntasRef } from './BoxPerguntas';
import BoxSolicitacoes from './BoxSolicitacoes';
import BoxSugestoesProtocolos from './BoxSugestoesProtocolos';
import ModalRedirecionarProtocolo from './BoxSugestoesProtocolos/ModalRedirecionarProtocolo';
import {
  Container,
  Content,
  DescriptionContainer,
  Footer,
  Header,
  RefazerButton,
} from './styles';
import { TipoPasso } from './types';

export interface PassoExecutadoDetailProps {
  passoExecutado: PassoExecutado;
}

export type PassoExecutadoDetailRef = HTMLDivElement;

const PassoExecutadoDetail = React.forwardRef<
  PassoExecutadoDetailRef,
  PassoExecutadoDetailProps
>(({ passoExecutado }, ref) => {
  const boxPerguntasRef = React.useRef<BoxPerguntasRef>(null);

  const { user } = useAuth();
  const history = useHistory();
  const [{ mode }, { dispatchStartFromExecution }] = useEnterpriseContext();
  const [
    { status, passosExecutados, protocoloExecutado, isProtocoloReadOnly },
    {
      dispatchExecutarPasso,
      dispatchRefazerPasso,
      dispatchPararPasso,
      dispatchProsseguirPasso,
      dispatchSolicitarFinalizacao,
      dispatchAlterarStatus,
      dispatchSetExecucao,
    },
  ] = useExecucaoContext();

  const [
    { generalStatus },
    ,
    { dispatchSolicitarRecursos, dispatchCancelarRecursos },
  ] = useIntegrationContext();

  const [payload, setPayload] = useState<RespostaPayload>(() =>
    getPassoRespostaPayload(passoExecutado),
  );
  const [selectedRedirects, setSelectedRedirects] = React.useState(() => {
    if (passoExecutado.redirecionamento) {
      return passoExecutado.redirecionamento.opcoes.filter(
        ({ selected }) => selected,
      );
    }

    return [];
  });

  const [{ allowNullItems }] = usePerguntaContext();

  const [inError, setInError] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState(passoExecutado.solicitacoes);
  const [isProsseguirLoading, setIsProsseguirLoading] = useState(false);
  const [isCancelandoArtefatos, setIsCancelandoArtefatos] = useState(false);
  const [redirectModalOpen, setRedirectModalOpen] = useState(false);

  const integracaoEnterpriseAtiva = useMemo(() => {
    return mode === 'integrated';
  }, [mode]);

  const canExecutar = useMemo(
    () =>
      status === 'active' &&
      isValidRespostaPayload(passoExecutado, payload, allowNullItems),
    [allowNullItems, passoExecutado, payload, status],
  );

  const enablePreescrever = useMemo(
    () => ['some_selected', 'all_selected', 'error'].includes(generalStatus),
    [generalStatus],
  );

  const enableRetry = useMemo(() => generalStatus === 'error', [generalStatus]);

  const exibeBreakPoint = useMemo(
    () =>
      passoExecutado.breakpoint &&
      passoExecutado.executado &&
      passoExecutado.id === passosExecutados.slice(-1)[0].id,
    [passoExecutado, passosExecutados],
  );

  const submitText = React.useMemo(() => {
    if (enablePreescrever || selectedRedirects.length >= 1) {
      return passoExecutado.final
        ? 'Prescrever e finalizar'
        : 'Prescrever e prosseguir';
    }

    return passoExecutado.final ? 'Finalizar e fechar' : 'Prosseguir';
  }, [enablePreescrever, passoExecutado.final, selectedRedirects.length]);

  const canRefazer = useMemo(() => {
    return (
      !isProtocoloReadOnly &&
      !exibeBreakPoint &&
      passoExecutado.executado &&
      !protocoloExecutado?.executado &&
      ((passoExecutado.executado_por &&
        passoExecutado.executado_por === user?.username) ||
        !passoExecutado.executado_por)
    );
  }, [
    exibeBreakPoint,
    isProtocoloReadOnly,
    passoExecutado.executado,
    passoExecutado.executado_por,
    protocoloExecutado?.executado,
    user?.username,
  ]);

  const passoType: TipoPasso = React.useMemo(() => {
    if (solicitacoes.some(({ tipo }) => tipo === 'condicao')) {
      return TipoPasso.CONDITION;
    }

    if (passoExecutado.breakpoint) {
      return TipoPasso.BREAKPOINT;
    }

    return TipoPasso.QUESTIONNARIE;
  }, [passoExecutado.breakpoint, solicitacoes]);

  const dispatch = useCallback(
    (dispachPayload: ExecutarPassoPayload) => {
      return {
        [TipoPasso.QUESTIONNARIE]: dispatchExecutarPasso,
        [TipoPasso.CONDITION]: dispatchExecutarPasso,
        [TipoPasso.BREAKPOINT]: dispatchPararPasso,
      }[passoType](dispachPayload);
    },
    [dispatchExecutarPasso, dispatchPararPasso, passoType],
  );

  const handleResposta = useCallback(
    (resposta: RespostaPayload) => {
      setPayload({ ...payload, ...resposta });
    },
    [payload],
  );

  const triggerExecutionFinishEvent = useCallback(
    (execucao?: ProtocoloExecutado) => {
      const eventObject = execucao || protocoloExecutado;
      if (passoExecutado.final && integracaoEnterpriseAtiva && eventObject) {
        // disabled - Goclin - Aguardando Implementação
        // medflowExecutionFinish(eventObject);
      }
    },
    [integracaoEnterpriseAtiva, passoExecutado.final, protocoloExecutado],
  );

  const handleRefazer = useCallback(async () => {
    let solicitacoesConduta: Solicitacao[] | undefined;

    if (solicitacoes.length) {
      try {
        setIsCancelandoArtefatos(true);
        const { results } = await IntegracaoService.requestCancelarArtefatos(
          passoExecutado,
          {
            solicitacoes: solicitacoes.map(solicitacao => solicitacao.id),
          },
        );
        setSolicitacoes(results);
        solicitacoesConduta = (results as unknown) as Solicitacao[];
      } finally {
        setIsCancelandoArtefatos(false);
      }
    }
    dispatchRefazerPasso({
      passoExecutado,
      conduta: { solicitacoes: solicitacoesConduta },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passoExecutado, dispatchRefazerPasso]);

  const directRedirect = useCallback(
    async (target: RedirecionamentoProtocolo) => {
      const { id: targetID, contexto_integracao } = target;
      const { id: targetIntegrationID } = contexto_integracao || {};

      if (mode === 'integrated' && targetIntegrationID) {
        const enterpriseURL = `/enterprise/integracoes/${targetIntegrationID}`;
        history.push(enterpriseURL);
      } else if (targetID) {
        const targetExecution = await ExecucaoAPI.getExecucao(
          targetID.toString(),
        );
        dispatchStartFromExecution(targetExecution);
        dispatchSetExecucao(targetExecution);
      }
    },
    [dispatchSetExecucao, dispatchStartFromExecution, history, mode],
  );

  const handleRedirecionar = useCallback(async () => {
    const { modo } = passoExecutado.redirecionamento || {};

    if (modo === ModoRedirecionamento.DIRETO && !redirectModalOpen) {
      setRedirectModalOpen(true);
      return;
    }

    setIsProsseguirLoading(true);

    const execucao = await ExecucaoAPI.redirecionarFluxo({
      passoExecutado,
      opcoes: selectedRedirects,
    });

    triggerExecutionFinishEvent(execucao);

    if (modo === ModoRedirecionamento.DIRETO) {
      const [destino] = execucao.destino;

      if (destino) directRedirect(destino);
    } else {
      toast(
        () => (
          <ToastContent Left={<FiCheck size={22} />}>
            Protocolos encaminhados com sucesso.
          </ToastContent>
        ),
        {
          position: toast.POSITION.BOTTOM_LEFT,
        },
      );
      dispatchProsseguirPasso({ passoExecutado, protocoloExecutado: execucao });
    }

    setIsProsseguirLoading(false);
  }, [
    directRedirect,
    dispatchProsseguirPasso,
    passoExecutado,
    redirectModalOpen,
    selectedRedirects,
    triggerExecutionFinishEvent,
  ]);

  const handlePrescreverExecutar = useCallback(async () => {
    try {
      if (passoExecutado.final && !integracaoEnterpriseAtiva) {
        dispatchSolicitarFinalizacao({ respostas: payload });

        return;
      }

      setIsProsseguirLoading(true);

      if (
        passoExecutado.solicitacoes.length &&
        generalStatus !== 'none_selected'
      ) {
        const response = await dispatchSolicitarRecursos(passoExecutado);
        if (response) {
          setSolicitacoes(response);

          await dispatch({
            passoExecutado,
            conduta: {
              respostas: payload,
              solicitacoes: (response as unknown) as Solicitacao[],
            },
          });
        }
      } else {
        await dispatch({ passoExecutado, conduta: { respostas: payload } });
      }
    } catch (error) {
      setInError(true);
      dispatchAlterarStatus({ status: 'on-error' });
    } finally {
      setIsProsseguirLoading(false);
      triggerExecutionFinishEvent();
    }
  }, [
    passoExecutado,
    integracaoEnterpriseAtiva,
    generalStatus,
    dispatchSolicitarFinalizacao,
    payload,
    dispatchSolicitarRecursos,
    dispatch,
    dispatchAlterarStatus,
    triggerExecutionFinishEvent,
  ]);

  const handleCancelarPrescricao = useCallback(() => {
    if (enableRetry) {
      dispatchCancelarRecursos();
    }
  }, [enableRetry, dispatchCancelarRecursos]);

  const handleOnProximaEtapa = useCallback(async () => {
    try {
      setIsProsseguirLoading(true);
      if (protocoloExecutado) {
        await dispatchProsseguirPasso({
          passoExecutado: passosExecutados.slice(-1)[0],
          protocoloExecutado,
        });
      }
    } finally {
      dispatchAlterarStatus({ status: 'active' });
      setIsProsseguirLoading(false);
    }
  }, [
    dispatchProsseguirPasso,
    dispatchAlterarStatus,
    passosExecutados,
    protocoloExecutado,
  ]);

  const handleRequireAnswers = React.useCallback(() => {
    if (!inError) {
      const items = listInvalidRespostaPayload(
        passoExecutado,
        payload,
        allowNullItems,
      );

      boxPerguntasRef.current?.require(items);
    }
  }, [allowNullItems, inError, passoExecutado, payload]);

  useEffect(() => {
    return () => {
      dispatchCancelarRecursos();
    };
  }, [dispatchCancelarRecursos]);

  const ConditionContent = (
    <ConditionAlert
      step={passoExecutado}
      readonly={isProtocoloReadOnly || passoExecutado.executado}
      conditions={
        solicitacoes.filter(
          solic => solic.tipo === 'condicao',
        ) as CondicaoSolicitacaoType[]
      }
      onConditionsConfirmed={conditions => {
        setSolicitacoes(prev =>
          prev.map(req => {
            const conditionConfirmed = conditions.find(
              condition => condition.id === req.id,
            );
            return (conditionConfirmed as FhirArtefatoType) || req;
          }),
        );

        handlePrescreverExecutar();
      }}
      onConditionsRefuted={() => {
        handlePrescreverExecutar();
      }}
    />
  );

  const QuestionnaireContent = (
    <>
      <Header>
        <h2>{passoExecutado.nome}</h2>
      </Header>

      <Content>
        {passoExecutado.descricao && (
          <DescriptionContainer>
            <Markdown text={passoExecutado.descricao} />
          </DescriptionContainer>
        )}

        {passoExecutado.redirecionamento && (
          <>
            {passoExecutado.redirecionamento.modo ===
              ModoRedirecionamento.DIRETO && (
              <ModalRedirecionarProtocolo
                isOpen={redirectModalOpen}
                onRedirecionar={handleRedirecionar}
                onClose={() => setRedirectModalOpen(false)}
              />
            )}
            <BoxSugestoesProtocolos
              disabled={passoExecutado.executado}
              options={passoExecutado.redirecionamento.opcoes}
              value={selectedRedirects}
              onChange={setSelectedRedirects}
              multiple={passoExecutado.redirecionamento.multipla_escolha}
            />
          </>
        )}

        {passoExecutado.perguntas.length > 0 && (
          <BoxPerguntas
            ref={boxPerguntasRef}
            passoExecutado={passoExecutado}
            handleResposta={handleResposta}
          />
        )}

        {passoExecutado.solicitacoes.length > 0 && (
          <BoxSolicitacoes
            passoExecutado={passoExecutado}
            solicitacoes={solicitacoes}
            integracaoEnterpriseAtiva={integracaoEnterpriseAtiva}
          />
        )}

        {passoExecutado.sumarizar_execucao && (
          <ProtocoloExecutadoSumario showBackToProtocol={false} />
        )}
      </Content>

      {passoExecutado.solicitacoes.length > 0 && generalStatus === 'error' && (
        <Information type="danger">
          <ErrorBox
            handleCancel={handleCancelarPrescricao}
            handleRetry={handlePrescreverExecutar}
          />
        </Information>
      )}

      {!passoExecutado.executado && (
        <Footer>
          <ButtonOpenFlowchart style={{ marginRight: 'auto' }} />

          <Feature
            name="INTERROMPER_PROTOCOLO"
            fallback={<ButtonSairProtocolo />}
          >
            <ButtonInterromperProtocolo />
          </Feature>
          <Button
            theme="primary"
            aria-disabled={!canExecutar}
            loading={isProsseguirLoading}
            onClick={() => {
              if (selectedRedirects.length >= 1) {
                handleRedirecionar();
              } else {
                handlePrescreverExecutar();
              }
            }}
            onAriaDisabledClick={handleRequireAnswers}
          >
            {submitText}
          </Button>
        </Footer>
      )}
    </>
  );

  const BreakPointContent = exibeBreakPoint ? (
    <PassoExecutadoBreakpoint handleOnProximaEtapa={handleOnProximaEtapa} />
  ) : (
    QuestionnaireContent
  );

  const PassoContentMap: { [T in TipoPasso]: JSX.Element } = {
    [TipoPasso.QUESTIONNARIE]: QuestionnaireContent,
    [TipoPasso.CONDITION]: ConditionContent,
    [TipoPasso.BREAKPOINT]: BreakPointContent,
  };

  return (
    <Container ref={ref} $showRefazerButton={canRefazer}>
      {PassoContentMap[passoType]}
      {passoExecutado.executado && canRefazer && (
        <Footer $noTopBorder>
          <RefazerButton
            theme="secondary"
            onClick={handleRefazer}
            loading={isCancelandoArtefatos}
            passoType={passoType}
          >
            Refazer este passo
          </RefazerButton>
        </Footer>
      )}
    </Container>
  );
});

export default PassoExecutadoDetail;
